/** @jsxImportSource @emotion/react */

import { useEffect, useState } from 'react';

interface IIdleDetector {
  enabled: boolean;
  timeToIdle: number;
}

const ACTIVITY_EVENTS = ['load', 'click', 'keydown', 'scroll'];

// 'visibilitychange' resets the idle countdown when the user returns to the tab.
// It fires on document, so it is registered there instead of on window.
const DOCUMENT_ACTIVITY_EVENTS = ['visibilitychange'];
let idleTimeout: number;

const useIdleDetector = ({ enabled, timeToIdle }: IIdleDetector) => {
  const [userIsIdle, setUserIsIdle] = useState(false);

  // Setup user activity event listeners.
  useEffect(() => {
    // On user activity, reset the current timeout and set a new one.
    const onUserActivity = () => {
      setUserIsIdle(false);
      clearTimeout(idleTimeout);

      if (enabled) {
        idleTimeout = window.setTimeout(() => {
          setUserIsIdle(true);
        }, timeToIdle * 1000);
      }
    };

    // Only the return-to-tab transition counts as activity — hiding the tab
    // must not reset (and thereby extend) the idle countdown.
    const onVisibilityChange = () => {
      if (!document.hidden) {
        onUserActivity();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onUserActivity, true);
    });
    DOCUMENT_ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, onVisibilityChange, true);
    });

    onUserActivity();

    // Clear timeout and remove event listeners.
    return () => {
      clearTimeout(idleTimeout);

      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onUserActivity, true);
      });
      DOCUMENT_ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, onVisibilityChange, true);
      });
    };
  }, [enabled, timeToIdle]);

  return {
    userIsIdle,
  };
};

export default useIdleDetector;
